import { createContext, useContext, useMemo, useReducer } from 'react'
import { useLocation } from 'react-router-dom'

import type { Dispatch, PropsWithChildren, ReactElement, Reducer } from 'react'
import type { DeviceType } from '~/types/device-type'
import type { IAP_FREE_TRIAL, IAP_PERIODICITY, IAP_TYPE, IApp } from '~/types/iap'
import type { IFilter, TFilterKey } from '~/types/iap-context'

import { getFiltersParamsByNames } from '~/utils/filters'

export interface IActionReducer {
  type: TFilterKey
  value?: unknown
}

interface IState {
  apps: IApp[]
  appsUnlocked: IApp[]
  features: string[]
  freeTrial: IAP_FREE_TRIAL[]
  period: IAP_PERIODICITY[]
  priceRange: {
    max: number
    min: number
  }
  skus: string[]
  type?: IAP_TYPE
}

export interface IIapContext {
  dispatch: Dispatch<IActionReducer>
  state: IState
}

export type IStateReducer = typeof defaultState

const reducer: Reducer<IState, IActionReducer> = (state, action) => {
  switch (action.type) {
    case 'apps':
      return { ...state, apps: action.value as IState['apps'] }
    case 'period':
      return { ...state, period: action.value as IState['period'] }
    case 'freeTrial':
      return { ...state, freeTrial: action.value as IState['freeTrial'] }
    case 'priceRange':
      return { ...state, priceRange: action.value as IState['priceRange'] }
    case 'type':
      return { ...state, type: action.value as IState['type'] }
    case 'appsUnlocked':
      return { ...state, appsUnlocked: action.value as IState['appsUnlocked'] }
    case 'skus':
      return { ...state, skus: action.value as IState['skus'] }
    case 'features':
      return { ...state, features: action.value as IState['features'] }
    case 'reset':
      return defaultState
    default:
      return state
  }
}

export const defaultState: IState = {
  apps: [],
  appsUnlocked: [],
  features: [],
  freeTrial: [],
  period: [],
  priceRange: {
    max: 200,
    min: 0,
  },
  skus: [],
  type: undefined,
}

export const getInitialState = (filters: IFilter): IStateReducer => {
  return {
    apps:
      filters.apps && Array.isArray(filters.apps)
        ? filters.apps.map((app: string) => {
            const [app_key, device_type] = app.split(':')
            return {
              app_key,
              device_type: device_type as DeviceType,
            }
          })
        : [],
    appsUnlocked:
      filters.appsUnlocked && Array.isArray(filters.appsUnlocked) && filters.appsUnlocked.length
        ? filters.appsUnlocked.map((app: string) => {
            const [app_key, device_type] = app.split(':')
            return {
              app_key,
              device_type: device_type as DeviceType,
            }
          })
        : defaultState.appsUnlocked,
    features: filters.features ? (filters.features as string[]) : defaultState.features,
    freeTrial: filters.freeTrial ? (filters.freeTrial as IAP_FREE_TRIAL[]) : defaultState.freeTrial,
    period: filters.period ? (filters.period as IAP_PERIODICITY[]) : defaultState.period,
    priceRange:
      filters.priceRange && (filters.priceRange as string[]).length > 0 && !!(filters.priceRange as string[])[0]
        ? {
            max: Number((filters.priceRange as string[])[0].split('/')[1]),
            min: Number((filters.priceRange as string[])[0].split('/')[0]),
          }
        : defaultState.priceRange,
    skus: filters.skus ? (filters.skus as string[]) : defaultState.skus,
    type: filters.type ? (filters.type as IAP_TYPE[])[0] : defaultState.type,
  }
}

export const IapContext = createContext<IIapContext>({
  dispatch: (): IStateReducer => defaultState,
  state: defaultState,
})

const IapContextProvider = ({ children }: PropsWithChildren): ReactElement => {
  /** Local state */

  const { search } = useLocation()

  const filters = useMemo(() => getFiltersParamsByNames(search, Object.keys(defaultState)), [search])

  const initialState = getInitialState(filters)

  const [state, dispatch] = useReducer(reducer, initialState)

  const context = useMemo(() => ({ dispatch, state }), [state])

  /** Render */

  return <IapContext.Provider value={context}>{children}</IapContext.Provider>
}

const useIapContext = (): IIapContext => {
  const context = useContext(IapContext)

  if (context === undefined) {
    throw new Error('useIapContext was used outside of its Provider')
  }

  return context
}

export { IapContextProvider, useIapContext }
